
import { Component, ChangeDetectionStrategy, signal, inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TranslatePipe } from '../../pipes/translate.pipe';
import { AuthService } from '../../services/auth.service';
import { DataService } from '../../services/data.service';
import { StorePartner } from '../../models/index';
import { NotificationService } from '../../services/notification.service';
import { ConfirmationModalComponent } from '../confirmation-modal/confirmation-modal.component';

@Component({
  selector: 'app-partner-network',
  standalone: true,
  templateUrl: './partner-network.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, FormsModule, TranslatePipe, ConfirmationModalComponent]
})
export class PartnerNetworkComponent implements OnInit {
  private authService = inject(AuthService);
  private dataService = inject(DataService);
  private notificationService = inject(NotificationService);

  userMode = signal<'PROVIDER' | 'CLIENT'>('PROVIDER'); // PROVIDER = Clinic, CLIENT = Dispensary
  partnerCodeInput = signal('');
  myPartnerCode = signal<string>('Loading...');
  
  connectedPartners = signal<StorePartner[]>([]);
  isLoading = signal(false);

  // Modal State
  confirmModalState = signal({ 
    isOpen: false, 
    title: '', 
    message: '', 
    confirmButtonText: 'Confirm',
    confirmButtonClass: 'bg-emerald-600 hover:bg-emerald-700',
    action: null as (() => Promise<void>) | null 
  });

  ngOnInit(): void {
    const currentStore = this.authService.currentStore();
    if (currentStore) {
        // Auto-detect mode based on Store Type
        if (currentStore.businessType === 'DISPENSARY') {
            this.userMode.set('CLIENT');
        } else {
            this.userMode.set('PROVIDER');
        }
        // Use Store ID as the shareable code
        this.myPartnerCode.set(currentStore.id);
        
        this.loadPartners();
    }
  }

  async loadPartners() {
    this.isLoading.set(true);
    try {
        const partners = await this.dataService.getPartners();
        this.connectedPartners.set(partners);
    } catch(e) {
        console.error("Failed to load partners", e);
    } finally {
        this.isLoading.set(false);
    }
  }

  copyCode() {
    navigator.clipboard.writeText(this.myPartnerCode());
    this.notificationService.showSuccess('Copied Store ID!');
  }

  async connectPartner() {
    const code = this.partnerCodeInput().trim();
    if(!code) return;

    if (code === this.myPartnerCode()) {
        this.notificationService.showError("You cannot connect to yourself.");
        return;
    }

    try {
        await this.dataService.requestPartnerConnection(code);
        this.notificationService.showSuccess('Connection request sent! Waiting for approval.');
        this.partnerCodeInput.set('');
        this.loadPartners();
    } catch (e: any) {
        this.notificationService.showError(`Failed to connect: ${e.message || 'Invalid Store ID'}`);
    }
  }

  acceptPartner(partnerId: string) {
      this.confirmModalState.set({
          isOpen: true,
          title: 'ยืนยันการรับคำขอ',
          message: 'คุณต้องการยอมรับคำขอเชื่อมต่อจากร้านค้านี้ใช่หรือไม่?',
          confirmButtonText: 'ยอมรับ (Accept)',
          confirmButtonClass: 'bg-emerald-600 hover:bg-emerald-700',
          action: async () => {
              try {
                  await this.dataService.updatePartnerStatus(partnerId, 'ACTIVE');
                  this.notificationService.showSuccess('เชื่อมต่อกับพันธมิตรสำเร็จ');
                  this.loadPartners();
              } catch(e: any) {
                  this.notificationService.showError("Error: " + e.message);
              }
          }
      });
  }

  rejectPartner(partnerId: string) {
    this.confirmModalState.set({
        isOpen: true,
        title: 'ยืนยันการปฏิเสธ',
        message: 'คุณต้องการปฏิเสธคำขอเชื่อมต่อนี้ใช่หรือไม่?',
        confirmButtonText: 'ปฏิเสธ (Reject)',
        confirmButtonClass: 'bg-red-600 hover:bg-red-700',
        action: async () => {
            try {
                await this.dataService.updatePartnerStatus(partnerId, 'REJECTED');
                this.notificationService.showSuccess('ปฏิเสธคำขอเรียบร้อยแล้ว');
                this.loadPartners();
            } catch(e: any) {
                this.notificationService.showError("Error: " + e.message);
            }
        }
    });
  }

  closeConfirmModal() {
    this.confirmModalState.update(s => ({ ...s, isOpen: false }));
  }

  async onConfirmAction() {
    const action = this.confirmModalState().action;
    if (action) await action();
    this.closeConfirmModal();
  }
}
